import axios from "../../interceptors/axiosInterceptor";
import {
  LOGIN_URL,
  GET_MFA_SESSION_URL,
  VALIDATE_MFA_URL,
  RENEW_ACCESS_TOKEN_URL,
  MFA_FALLBACK_URL,
  ACCESS_TOKEN_GENERATION_URI,
  RENEW_ACCESS_TOKEN_OIDC_URL,
} from "../../config/Url";
import {
  IloginRequestPayload,
  IloginResponse,
  ILoginResponseNormalized,
  IMFASessionPayload,
  IMFASessionResponse,
  IMFASessionResponseNormalized,
  IMFASessionValidatePayload,
  IMFAFallbackPayload,
  IMFASessionValidateResponse,
  IMFAFallbackResponse,
  IRenewAccessTokenPayload,
  IKeycloakAuthExchangeRequestPayload,
  IRenewAccessTokenOIDCPayload,
} from "../../interfaces/Index";
import {
  loginPayloadNormalizer,
  loginOIDCPayloadNormalizer,
} from "../../normalizer/AuthNormalizer";
import { saveToLocal } from "../../utils/Cache";
import { normalizeCatchErrors } from "../../utils/errorUtils";
import ability from "../../config/ability";
import {
  BAZAAR_HEADER_KEY,
  BAZAAR_SECRET_KEY,
  LOCAL_STORAGE_KEYS,
  SOMETHING_WENT_WRONG,
} from "../../config/constants";

export const loginApiCall = (payload: IloginRequestPayload) =>
  new Promise((resolve, reject) => {
    const headers = {
      [BAZAAR_HEADER_KEY]: BAZAAR_SECRET_KEY,
    };
    axios
      .post(LOGIN_URL, payload, { headers })
      .then((response) => {
        if (response) {
          const data: any = response && response.data;
          const payload = loginPayloadNormalizer(data);
          // Updating the ability context
          ability.update(payload.scopes);

          // Saving auth to local storage (cache)
          saveToLocal(payload, LOCAL_STORAGE_KEYS.auth);

          setTimeout(() => {
            resolve(payload);
          }, 200);
        } else {
          reject(SOMETHING_WENT_WRONG);
        }
      })
      .catch((error) => {
        const errorPayload = normalizeCatchErrors(error.response);
        reject(errorPayload);
      });
  });

export const mfaSessionApiCall = (payload: IMFASessionPayload) =>
  new Promise<IMFASessionResponseNormalized>((resolve, reject) => {
    const headers = {
      [BAZAAR_HEADER_KEY]: BAZAAR_SECRET_KEY,
    };
    axios
      .post(GET_MFA_SESSION_URL, payload, { headers })
      .then((response) => {
        if (response) {
          const data: IMFASessionResponse = response.data;
          // normalize mfa factors
          const normalizedData: IMFASessionResponseNormalized = {
            token: data.token,
            factors: data.mfaSteps.map(function (step) {
              return {
                name: step.name,
                fallbacks: step.fallbacks,
              };
            }),
          };
          resolve(normalizedData);
        } else {
          reject(SOMETHING_WENT_WRONG);
        }
      })
      .catch((error) => {
        const errorPayload = normalizeCatchErrors(error.response);
        reject(errorPayload);
      });
  });

export const mfaSessionValidateApiCall = (
  payload: IMFASessionValidatePayload
) =>
  new Promise<IMFASessionValidateResponse | ILoginResponseNormalized>(
    (resolve, reject) => {
      axios
        .post(VALIDATE_MFA_URL, payload)
        .then((response) => {
          if (response) {
            const data: IloginResponse | IMFASessionValidateResponse =
              response.data;
            if ((data as IMFASessionValidateResponse).mfaToken) {
              resolve(data as IMFASessionValidateResponse);
            } else {
              const payload = loginPayloadNormalizer(data as IloginResponse);
              // Updating the ability context
              ability.update(payload.scopes);

              // Saving auth to local storage (cache)
              saveToLocal(payload, LOCAL_STORAGE_KEYS.auth);

              setTimeout(() => {
                resolve(payload);
              }, 200);
            }
          } else {
            reject(SOMETHING_WENT_WRONG);
          }
        })
        .catch((error) => {
          const errorPayload = normalizeCatchErrors(error.response);
          reject(errorPayload);
        });
    }
  );

export const mfaSessionFallbackApiCall = (payload: IMFAFallbackPayload) =>
  new Promise<IMFAFallbackResponse | ILoginResponseNormalized>(
    (resolve, reject) => {
      axios
        .post(MFA_FALLBACK_URL, payload)
        .then((response) => {
          if (response) {
            const data: IloginResponse | IMFAFallbackResponse = response.data;
            resolve(data as IMFAFallbackResponse);
          } else {
            reject(SOMETHING_WENT_WRONG);
          }
        })
        .catch((error) => {
          const errorPayload = normalizeCatchErrors(error.response);
          reject(errorPayload);
        });
    }
  );

export const refreshAccessToken = (payload: IRenewAccessTokenPayload) =>
  new Promise<ILoginResponseNormalized>((resolve, reject) => {
    const headers = {
      [BAZAAR_HEADER_KEY]: BAZAAR_SECRET_KEY,
    };
    axios
      .post(RENEW_ACCESS_TOKEN_URL, payload, { headers })
      .then((response) => {
        if (response) {
          const data: IloginResponse = response.data;
          const normalizedPayload = loginPayloadNormalizer(
            data as IloginResponse
          );
          // Updating the ability context
          ability.update(normalizedPayload.scopes);

          if (!normalizedPayload.auth.refreshToken) {
            normalizedPayload.auth.refreshToken = payload.token;
          }

          // Saving auth to local storage (cache)
          saveToLocal(normalizedPayload, LOCAL_STORAGE_KEYS.auth);

          setTimeout(() => {
            resolve(normalizedPayload);
          }, 200);
        } else {
          reject(SOMETHING_WENT_WRONG);
        }
      })
      .catch((error) => {
        const errorPayload = normalizeCatchErrors(error.response);
        reject(errorPayload);
      });
  });

export const refreshAccessTokenOIDC = (payload: IRenewAccessTokenOIDCPayload) =>
  new Promise<ILoginResponseNormalized>((resolve, reject) => {
    const headers = {
      [BAZAAR_HEADER_KEY]: BAZAAR_SECRET_KEY,
    };
    axios
      .post(RENEW_ACCESS_TOKEN_OIDC_URL, payload, { headers })
      .then((response) => {
        if (response) {
          const data: IloginResponse = response.data;
          const normalizedPayload = loginOIDCPayloadNormalizer(
            data as IloginResponse
          );
          // Updating the ability context
          ability.update(normalizedPayload.scopes);

          if (!normalizedPayload.auth.refreshToken) {
            normalizedPayload.auth.refreshToken = payload.refreshToken;
          }

          // Saving auth to local storage (cache)
          saveToLocal(normalizedPayload, LOCAL_STORAGE_KEYS.auth);

          setTimeout(() => {
            resolve(normalizedPayload);
          }, 200);
        } else {
          reject(SOMETHING_WENT_WRONG);
        }
      })
      .catch((error) => {
        const errorPayload = normalizeCatchErrors(error.response);
        reject(errorPayload);
      });
  });

export const validateKeycloakSession = (
  payload: IKeycloakAuthExchangeRequestPayload
) =>
  new Promise((resolve, reject) => {
    axios
      .post(ACCESS_TOKEN_GENERATION_URI, payload)
      .then((response) => {
        if (response) {
          const data = response && response.data;
          const payload = loginOIDCPayloadNormalizer(data as IloginResponse);
          ability.update(payload.scopes);

          // Saving auth to local storage (cache)
          saveToLocal(payload, LOCAL_STORAGE_KEYS.auth);

          setTimeout(() => {
            resolve(payload);
          }, 200);
        } else {
          reject(SOMETHING_WENT_WRONG);
        }
      })
      .catch((error) => {
        const errorPayload = normalizeCatchErrors(error.response);
        reject(errorPayload);
      });
  });
